import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSession } from '../hooks/useSession';
import { useLiveCall } from '../hooks/useLiveCall';
import { ApiService } from '../services/api';
import { Venue, VenuePackage, StageHistoryItem, CardViewMode } from '../types';
import { runStageViewTransition } from '../utils/viewTransitions';

import { SpotlightGridDeck } from '../components/decks/SpotlightGridDeck';
import { VenueShowcaseDeck, ShowcaseFocusView } from '../components/decks/VenueShowcaseDeck';
import { PriceComparisonDeck } from '../components/decks/PriceComparisonDeck';
import { AvailabilityCalculatorDeck } from '../components/decks/AvailabilityCalculatorDeck';
import { ConfirmationGateDeck } from '../components/decks/ConfirmationGateDeck';
import { BookingTrackerDeck } from '../components/decks/BookingTrackerDeck';

import { FloatingCallDock } from '../components/dock/FloatingCallDock';
import { SaveSessionModal } from '../components/common/SaveSessionModal';

import { resolveVoiceSpotlightVenueId } from '../utils/voiceSpotlight';
export { resolveVoiceSpotlightVenueId };

const EMPTY_METRICS: string[] = [];

export const CustomerStagePage: React.FC = () => {
  const { deviceId, sessionId } = useSession();
  const [catalogVenues, setCatalogVenues] = useState<Venue[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [activeCorridorFilter, setActiveCorridorFilter] = useState<string>('All');
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [, setStageHistory] = useState<StageHistoryItem[]>([]);
  const lastRecordedSigRef = useRef<string>('');
  const catalogVenuesRef = useRef<Venue[]>([]);
  catalogVenuesRef.current = catalogVenues;
  const stageDataRef = useRef<any>(null);
  const isSteppingBackRef = useRef<boolean>(false);
  const lastHistoryKeyRef = useRef<string>('SPOTLIGHT_GRID:All::');
  const historyDepthRef = useRef<number>(0);
  const ignoreNextPopStateRef = useRef<boolean>(false);

  const popBrowserHistoryIfNeeded = useCallback((fromPopState?: boolean) => {
    if (fromPopState === true) {
      historyDepthRef.current = Math.max(0, historyDepthRef.current - 1);
      return;
    }
    if (historyDepthRef.current > 0 && typeof window !== 'undefined' && typeof window.history?.back === 'function') {
      historyDepthRef.current = Math.max(0, historyDepthRef.current - 1);
      ignoreNextPopStateRef.current = true;
      window.history.back();
    }
  }, []);

  const {
    callState,
    setCallState,
    isMuted,
    stageMode,
    stageData,
    spokenCueText,
    setStageMode,
    setStageData,
    errorMessage,
    waveformHeights,
    startCall,
    endCall,
    toggleMute,
    handleInterrupt,
    resumeFromInactivity,
    sendTouchAction
  } = useLiveCall({
    deviceId,
    sessionId,
    initialStageMode: 'SPOTLIGHT_GRID'
  });
  stageDataRef.current = stageData;
  const stageModeRef = useRef(stageMode);
  stageModeRef.current = stageMode;
  const activeCorridorFilterRef = useRef(activeCorridorFilter);
  activeCorridorFilterRef.current = activeCorridorFilter;

  const handleSelectCorridorPill = useCallback((corridor: string) => {
    if (corridor === 'All' && (activeCorridorFilterRef.current !== 'All' || stageDataRef.current?.searchFilter)) {
      isSteppingBackRef.current = true;
      popBrowserHistoryIfNeeded(false);
    }
    setActiveCorridorFilter(corridor);
    setStageData((prev: any) => ({
      ...prev,
      venues: catalogVenuesRef.current.length > 0 ? catalogVenuesRef.current : prev?.venues,
      spotlightVenues: catalogVenuesRef.current.length > 0 ? catalogVenuesRef.current : prev?.spotlightVenues,
      searchFilter:
        corridor === 'All'
          ? null
          : {
              ...(prev?.searchFilter || {}),
              area: corridor,
              areas: [corridor]
            }
    }));
  }, [setStageData, popBrowserHistoryIfNeeded]);

  const handleCompareVenues = useCallback((venue?: Venue) => {
    const currentStageData = stageDataRef.current;
    const currentCatalog = catalogVenuesRef.current;
    const baseList =
      currentStageData?.venues?.length >= 4
        ? currentStageData.venues
        : currentCatalog.length > 0
          ? currentCatalog
          : currentStageData?.venues || [];
    let compareCandidates = baseList.slice(0, 3);
    if (venue && baseList.length > 0) {
      const others = baseList.filter((v: Venue) => v.id !== venue.id).slice(0, 2);
      compareCandidates = [venue, ...others];
    }
    runStageViewTransition(() => {
      if (venue) setSelectedVenue(venue);
      setStageMode('PRICE_COMPARISON');
      setStageData((prev: any) => ({ ...prev, compareVenues: compareCandidates }));
    }, 'scene-morph');
    sendTouchAction('compare_venues', { venueId: venue?.id });
  }, [setStageMode, setStageData, sendTouchAction]);

  // Listen for top-bar "Save / Email Session" button event & programmatic stage commands
  useEffect(() => {
    const handleOpenSave = () => setIsSaveModalOpen(true);
    const handleStageCommand = (e: Event) => {
      const detail = (e as CustomEvent)?.detail || {};
      if (detail.callState) {
        setCallState(detail.callState);
      }
      if (detail.voiceSpotlightVenueId !== undefined) {
        setStageData((prev: any) => ({
          ...prev,
          voiceSpotlightVenueId: detail.voiceSpotlightVenueId
        }));
      }
      if (detail.focusView) {
        setStageData((prev: any) => ({
          ...prev,
          focusView: detail.focusView
        }));
      }
      if (detail.corridor) {
        handleSelectCorridorPill(detail.corridor);
        setStageMode('SPOTLIGHT_GRID');
      }
      if (detail.mode === 'PRICE_COMPARISON') {
        handleCompareVenues(selectedVenue || catalogVenues[0]);
      } else if (detail.mode) {
        setStageMode(detail.mode);
        setStageData((prev: any) => ({
          ...prev,
          venue: prev?.venue || selectedVenue || catalogVenuesRef.current[0]
        }));
      }
    };
    window.addEventListener('mehfil:open-save-session', handleOpenSave);
    window.addEventListener('mehfil:stage-command', handleStageCommand);
    return () => {
      window.removeEventListener('mehfil:open-save-session', handleOpenSave);
      window.removeEventListener('mehfil:stage-command', handleStageCommand);
    };
  }, [selectedVenue, catalogVenues, handleSelectCorridorPill, handleCompareVenues, setStageMode, setStageData, setCallState]);

  // Load initial Karachi venue catalog on mount
  useEffect(() => {
    let active = true;
    ApiService.searchVenues({ limit: 700 })
      .then((res) => {
        if (active && res?.venues?.length > 0) {
          setCatalogVenues(res.venues);
          setSelectedVenue((prev) => prev || res.venues[0]);
          setStageData((prev: any) =>
            prev?.venues?.length || prev?.searchFilter ? prev : { ...prev, venues: res.venues }
          );
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [setStageData]);

  // Synchronize selectedVenue & corridor filter when voice tool updates stageData
  useEffect(() => {
    if (stageData?.venue) {
      setSelectedVenue(stageData.venue);
    }
    if (Array.isArray(stageData?.searchFilter?.areas) && stageData.searchFilter.areas.length === 1) {
      setActiveCorridorFilter(stageData.searchFilter.areas[0]);
    } else if (Array.isArray(stageData?.searchFilter?.areas) && stageData.searchFilter.areas.length > 1) {
      setActiveCorridorFilter('All');
    } else if (stageData?.searchFilter?.area) {
      setActiveCorridorFilter(stageData.searchFilter.area);
    }

    const activeCardView: CardViewMode =
      stageData?.cardView ||
      (stageData?.focusView === 'packages'
        ? 'packages'
        : stageData?.focusView === 'photos'
          ? 'photos'
          : stageData?.focusView === 'amenities'
            ? 'amenities'
            : stageMode === 'PRICE_COMPARISON'
              ? 'price_comparison'
              : 'discovery');

    const label =
      stageData?.screenContext?.activeHeading ||
      stageData?.blueprint?.title ||
      (stageMode === 'VENUE_SHOWCASE' && stageData?.venue?.name
        ? `${stageData.venue.name} (${stageData.focusView || 'overview'})`
        : stageMode === 'PRICE_COMPARISON'
          ? 'Price Comparison Arena'
          : 'Venue Spotlight');

    const sig = `${stageMode}:${activeCardView}:${label}`;
    if (sig !== lastRecordedSigRef.current) {
      lastRecordedSigRef.current = sig;
      setStageHistory((prev) => {
        const nextItem: StageHistoryItem = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          mode: stageMode,
          cardView: activeCardView,
          label: label.length > 34 ? `${label.slice(0, 32)}…` : label,
          data: stageData,
          timestamp: Date.now()
        };
        const deduped = prev.filter(
          (item) => item.mode !== stageMode && item.label !== nextItem.label
        );
        return [nextItem, ...deduped].slice(0, 6);
      });
    }
  }, [stageMode, stageData]);

  // Push browser history entries on forward stage/filter transitions so Browser Back & mobile swipe-back step backward cleanly
  useEffect(() => {
    const filterSig = stageData?.searchFilter
      ? JSON.stringify({
          a: stageData.searchFilter.areas || stageData.searchFilter.area || '',
          q: stageData.searchFilter.query || stageData.searchFilter.venueName || '',
          t: stageData.searchFilter.venueTypes || stageData.searchFilter.venueType || '',
          g: stageData.searchFilter.minGuests || ''
        })
      : '';
    const venueSig = stageMode === 'VENUE_SHOWCASE' || stageMode === 'AVAILABILITY_CALCULATOR' || stageMode === 'CONFIRMATION_GATE'
      ? (stageData?.venue?.id || selectedVenue?.id || '')
      : '';
    const historyKey = `${stageMode}:${activeCorridorFilter}:${venueSig}:${filterSig}`;

    if (isSteppingBackRef.current) {
      isSteppingBackRef.current = false;
      lastHistoryKeyRef.current = historyKey;
      return;
    }

    const isUnfilteredRoot =
      stageMode === 'SPOTLIGHT_GRID' &&
      activeCorridorFilter === 'All' &&
      !stageData?.searchFilter;

    if (historyKey !== lastHistoryKeyRef.current) {
      lastHistoryKeyRef.current = historyKey;
      if (!isUnfilteredRoot && typeof window !== 'undefined' && window.history?.pushState) {
        historyDepthRef.current += 1;
        window.history.pushState(
          {
            mehfilStage: true,
            stageMode,
            corridor: activeCorridorFilter,
            venueId: venueSig || null,
            hasSearchFilter: Boolean(stageData?.searchFilter),
            depth: historyDepthRef.current
          },
          ''
        );
      }
    }
  }, [stageMode, activeCorridorFilter, stageData?.searchFilter, stageData?.venue?.id, selectedVenue?.id]);

  // Multimodal Handlers wrapped in Apple/Google View Transitions
  const handleSelectVenue = useCallback((venue: Venue, initialFocusView: ShowcaseFocusView = 'overview') => {
    runStageViewTransition(
      () => {
        setSelectedVenue(venue);
        setStageMode('VENUE_SHOWCASE');
        setStageData((prev: any) => ({
          ...prev,
          venue,
          focusView: initialFocusView
        }));
      },
      initialFocusView === 'packages' ? 'card-expand' : 'scene-morph'
    );

    const applyFreshVenue = (freshVenue: Venue) => {
      setSelectedVenue((prev) => (prev && prev.id === freshVenue.id ? freshVenue : prev));
      setStageData((prev: any) =>
        prev?.venue?.id === freshVenue.id ? { ...prev, venue: freshVenue } : prev
      );
      setCatalogVenues((prev) => prev.map((v) => (v.id === freshVenue.id ? freshVenue : v)));
    };

    // Fetch fresh venue details to trigger on-demand background hydration when needed
    ApiService.getVenue(venue.slug || venue.id)
      .then((res) => {
        if (res?.venue) {
          applyFreshVenue(res.venue);
          const hasPendingRefs = Boolean(
            (res.venue.socialLinks as any)?.pending_photo_refs?.length > 0
          );
          if ((res.venue.images?.length || 0) < 3 && hasPendingRefs) {
            setTimeout(() => {
              ApiService.getVenue(venue.slug || venue.id)
                .then((followUp) => {
                  if (followUp?.venue) {
                    applyFreshVenue(followUp.venue);
                  }
                })
                .catch(() => {});
            }, 2600);
          }
        }
      })
      .catch(() => {});
    sendTouchAction('view_venue_details', {
      venueId: venue.id,
      venueName: venue.name,
      focusView: initialFocusView
    });
  }, [setStageMode, setStageData, sendTouchAction]);

  const handleCheckAvailability = useCallback((venue: Venue, pkg?: VenuePackage) => {
    runStageViewTransition(() => {
      setSelectedVenue(venue);
      setStageMode('AVAILABILITY_CALCULATOR');
      setStageData((prev: any) => ({ ...prev, venue, selectedPackage: pkg || null }));
    }, 'forward');
    sendTouchAction('calculate_budget', { venueId: venue.id, venueName: venue.name, packageId: pkg?.id });
  }, [setStageMode, setStageData, sendTouchAction]);

  const handleProceedToConfirmation = (config: {
    venue: Venue;
    selectedPackage: VenuePackage;
    guestCount: number;
    eventDate: string;
    slot: 'lunch' | 'dinner';
    totalEstimatedPkr: number;
    addonsTotalPkr?: number;
  }) => {
    runStageViewTransition(() => {
      setSelectedVenue(config.venue);
      setStageMode('CONFIRMATION_GATE');
      setStageData((prev: any) => ({
        ...prev,
        draft: {
          venueId: config.venue.id,
          venueName: config.venue.name,
          packageId: config.selectedPackage.id,
          packageName: config.selectedPackage.name,
          perHeadPrice: config.selectedPackage.pricePkr,
          guestCount: config.guestCount,
          date: config.eventDate,
          slot: config.slot,
          totalEstimatedPkr: config.totalEstimatedPkr,
          addonsTotalPkr: config.addonsTotalPkr || 0,
          requiresConfirmation: true
        }
      }));
    }, 'forward');
    sendTouchAction('prepare_inquiry_draft', {
      venueId: config.venue.id,
      guestCount: config.guestCount,
      eventDate: config.eventDate,
      slot: config.slot
    });
  };

  const handleConfirmInquiry = async (contactPayload: {
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    date: string;
    guestCount: number;
    slot: 'lunch' | 'dinner';
    eventType?: string;
    customerNotes?: string;
  }) => {
    const draft = stageData?.draft || {};
    const preparedDraft = await ApiService.prepareDraft({
      sessionId,
      venueId: draft.venueId || selectedVenue?.id || 'v-karsaz-01',
      eventType: contactPayload.eventType || 'barat',
      eventDate: contactPayload.date,
      slot: contactPayload.slot,
      guestCount: contactPayload.guestCount,
      customerName: contactPayload.customerName,
      customerPhone: contactPayload.customerPhone,
      customerEmail: contactPayload.customerEmail,
      packageId: draft.packageId
    });

    const committed = await ApiService.commitInquiry(preparedDraft.draftBookingId, 'tap');

    runStageViewTransition(() => {
      setStageMode('BOOKING_TRACKER');
      setStageData((prev: any) => ({ ...prev, booking: committed }));
    }, 'forward');
    sendTouchAction('commit_venue_inquiry', { bookingCode: committed.bookingCode });
  };

  const handleSendReplyToOwner = async (bookingCode: string, message: string) => {
    sendTouchAction('reply_to_owner', { bookingCode, message });
    if (stageData?.booking) {
      const msgs = stageData.booking.inquiryMessages || [];
      setStageData({
        ...stageData,
        booking: {
          ...stageData.booking,
          inquiryMessages: [
            ...msgs,
            {
              id: `temp-${Date.now()}`,
              bookingId: stageData.booking.id,
              sessionId,
              venueId: stageData.booking.venueId,
              senderRole: 'customer',
              senderName: 'You',
              messageText: message,
              createdAt: new Date().toISOString()
            }
          ]
        }
      });
    }
  };

  const handleQuickPrompt = useCallback((promptText: string, filterArea?: string) => {
    const currentCatalog = catalogVenuesRef.current;
    if (filterArea === 'COMPARE') {
      handleCompareVenues(selectedVenue || currentCatalog[0]);
      return;
    }
    if (filterArea) {
      runStageViewTransition(() => {
        setActiveCorridorFilter(filterArea);
        setStageMode('SPOTLIGHT_GRID');
        setStageData((prev: any) => ({
          ...prev,
          venues: currentCatalog,
          searchFilter: {
            area: filterArea,
            areas: filterArea === 'All' ? [] : [filterArea]
          },
          cardView: 'discovery'
        }));
      }, 'scene-morph');
      sendTouchAction('filter_corridor', { corridor: filterArea, promptText });
    }
  }, [handleCompareVenues, selectedVenue, setStageMode, setStageData, sendTouchAction]);

  // Ensure SPOTLIGHT_GRID never gets stuck with a 2-3 card comparison slice when no voice search filter is active
  const activeVenuesList = useMemo(
    () =>
      Array.isArray(stageData?.venues) &&
      stageData.venues.length > 0 &&
      (stageData.searchFilter || stageData.venues.length >= 4 || catalogVenues.length === 0)
        ? stageData.venues
        : catalogVenues,
    [stageData?.venues, stageData?.searchFilter, catalogVenues]
  );

  const compareVenuesList = useMemo(
    () =>
      stageData?.compareVenues?.length >= 2
        ? stageData.compareVenues
        : stageData?.venues?.length >= 2 && stageData.venues.length <= 4
          ? stageData.venues
          : activeVenuesList.slice(0, 3),
    [stageData?.compareVenues, stageData?.venues, activeVenuesList]
  );

  const returnToSpotlightGrid = useCallback((fromPopState?: boolean) => {
    isSteppingBackRef.current = true;
    popBrowserHistoryIfNeeded(fromPopState === true);
    const currentCatalog = catalogVenuesRef.current;
    runStageViewTransition(() => {
      setStageMode('SPOTLIGHT_GRID');
      setStageData((prev: any) => {
        const currentVenues = prev?.spotlightVenues || prev?.venues;
        const shouldUseCatalog =
          !prev?.searchFilter ||
          !Array.isArray(currentVenues) ||
          (currentVenues.length < 4 && currentCatalog.length >= 4);
        const restored = shouldUseCatalog && currentCatalog.length > 0 ? currentCatalog : currentVenues;
        return {
          ...prev,
          venues: restored,
          spotlightVenues: restored,
          searchFilter: shouldUseCatalog ? null : prev?.searchFilter
        };
      });
    }, 'backward');
    sendTouchAction('back_to_spotlight');
  }, [setStageMode, setStageData, sendTouchAction, popBrowserHistoryIfNeeded]);

  const stepBackToShowcase = useCallback((fromPopState?: boolean) => {
    isSteppingBackRef.current = true;
    popBrowserHistoryIfNeeded(fromPopState === true);
    runStageViewTransition(() => {
      setStageMode('VENUE_SHOWCASE');
    }, 'backward');
    sendTouchAction('back_to_showcase');
  }, [setStageMode, sendTouchAction, popBrowserHistoryIfNeeded]);

  const stepBackToCalculator = useCallback((fromPopState?: boolean) => {
    isSteppingBackRef.current = true;
    popBrowserHistoryIfNeeded(fromPopState === true);
    runStageViewTransition(() => {
      setStageMode('AVAILABILITY_CALCULATOR');
    }, 'backward');
    sendTouchAction('back_to_calculator');
  }, [setStageMode, sendTouchAction, popBrowserHistoryIfNeeded]);

  // Silent Voice-Synchronized "Finger Point" Card Spotlight (zero text subtitles on screen)
  const voiceSpotlightVenueId = useMemo(() => {
    if (callState !== 'speaking' && callState !== 'listening') {
      return stageData?.voiceSpotlightVenueId || null;
    }
    return resolveVoiceSpotlightVenueId(spokenCueText, activeVenuesList, stageData?.voiceSpotlightVenueId);
  }, [spokenCueText, callState, activeVenuesList, stageData?.voiceSpotlightVenueId]);

  const handleClearVoiceFilter = useCallback((fromPopState?: boolean) => {
    isSteppingBackRef.current = true;
    popBrowserHistoryIfNeeded(fromPopState === true);
    const currentCatalog = catalogVenuesRef.current;
    runStageViewTransition(() => {
      setActiveCorridorFilter('All');
      setStageData((prev: any) => ({
        ...prev,
        venues: currentCatalog,
        spotlightVenues: currentCatalog,
        searchFilter: null,
        cardView: 'discovery',
        focusMetrics: EMPTY_METRICS
      }));
    }, 'scene-morph');
    sendTouchAction('clear_voice_filter', { corridor: 'All' });
  }, [setStageData, sendTouchAction, popBrowserHistoryIfNeeded]);

  // Intercept Browser Back button & mobile swipe-back gesture (popstate) to step backward through stages & filters
  useEffect(() => {
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      window.history.replaceState(
        { mehfilStage: true, stageMode: 'SPOTLIGHT_GRID', corridor: 'All', depth: 0 },
        ''
      );
    }

    const handlePopState = () => {
      if (ignoreNextPopStateRef.current) {
        ignoreNextPopStateRef.current = false;
        return;
      }

      const currentMode = stageModeRef.current;
      const currentCorridor = activeCorridorFilterRef.current;
      const currentStageData = stageDataRef.current;

      if (currentMode === 'CONFIRMATION_GATE') {
        stepBackToCalculator(true);
      } else if (currentMode === 'AVAILABILITY_CALCULATOR') {
        stepBackToShowcase(true);
      } else if (
        currentMode === 'VENUE_SHOWCASE' ||
        currentMode === 'PRICE_COMPARISON' ||
        currentMode === 'BOOKING_TRACKER'
      ) {
        returnToSpotlightGrid(true);
      } else if (
        currentMode === 'SPOTLIGHT_GRID' &&
        (currentCorridor !== 'All' || Boolean(currentStageData?.searchFilter))
      ) {
        handleClearVoiceFilter(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [returnToSpotlightGrid, stepBackToShowcase, stepBackToCalculator, handleClearVoiceFilter]);

  return (
    <div className={`customer-stage-viewport ${stageMode === 'SPOTLIGHT_GRID' ? 'mode-spotlight' : ''}`.trim()}>
      {/* Multimodal Active Stage Decks */}
      {stageMode === 'SPOTLIGHT_GRID' && (
        <SpotlightGridDeck
          venues={activeVenuesList}
          allVenues={catalogVenues}
          initialCorridor={activeCorridorFilter}
          searchFilter={stageData?.searchFilter}
          cardView={stageData?.cardView}
          focusMetrics={stageData?.focusMetrics || EMPTY_METRICS}
          voiceSpotlightVenueId={voiceSpotlightVenueId}
          blueprint={stageData?.blueprint}
          relaxationNotice={stageData?.relaxationNotice}
          onSelectVenue={handleSelectVenue}
          onCompareVenue={handleCompareVenues}
          onCheckAvailability={handleCheckAvailability}
          onSelectCorridor={handleSelectCorridorPill}
          onClearVoiceFilter={() => handleClearVoiceFilter(false)}
          onTouchAction={sendTouchAction}
        />
      )}

      {stageMode === 'VENUE_SHOWCASE' && (
        <VenueShowcaseDeck
          venue={stageData?.venue || selectedVenue || activeVenuesList[0]}
          focusView={stageData?.focusView || 'overview'}
          initialPhotoCategory={stageData?.photoCategory || 'all'}
          initialGuestCount={stageData?.guestCount || 350}
          focusMetrics={stageData?.focusMetrics || EMPTY_METRICS}
          blueprint={stageData?.blueprint}
          onCheckAvailability={handleCheckAvailability}
          onCompareWithOthers={handleCompareVenues}
          onBackToSpotlight={() => returnToSpotlightGrid(false)}
          onTouchAction={sendTouchAction}
        />
      )}

      {stageMode === 'PRICE_COMPARISON' && (
        <PriceComparisonDeck
          venues={compareVenuesList}
          allVenues={activeVenuesList}
          guestCount={stageData?.guestCount || 350}
          initialMenuTier={stageData?.menuTier || 'chicken_1dish'}
          initialIncludeDecorAddon={stageData?.includeDecorAddon ?? true}
          initialIncludeGenFuel={stageData?.includeGenFuel ?? false}
          focusMetrics={stageData?.focusMetrics || EMPTY_METRICS}
          voiceSpotlightVenueId={voiceSpotlightVenueId}
          blueprint={stageData?.blueprint}
          relaxationNotice={stageData?.relaxationNotice}
          onSelectVenue={handleCheckAvailability}
          onBack={() => returnToSpotlightGrid(false)}
          onTouchAction={sendTouchAction}
        />
      )}

      {stageMode === 'AVAILABILITY_CALCULATOR' && (
        <AvailabilityCalculatorDeck
          venue={stageData?.venue || selectedVenue || activeVenuesList[0]}
          initialPackage={stageData?.selectedPackage}
          onProceedToConfirmation={handleProceedToConfirmation}
          onBack={() => stepBackToShowcase(false)}
          onTouchAction={sendTouchAction}
        />
      )}

      {stageMode === 'CONFIRMATION_GATE' && (
        <ConfirmationGateDeck
          draftData={stageData}
          onConfirm={handleConfirmInquiry}
          onCancel={() => stepBackToCalculator(false)}
          onTouchAction={sendTouchAction}
        />
      )}

      {stageMode === 'BOOKING_TRACKER' && (
        <BookingTrackerDeck
          bookingData={stageData}
          onSendReplyToOwner={handleSendReplyToOwner}
          onBack={() => returnToSpotlightGrid(false)}
          onTouchAction={sendTouchAction}
        />
      )}

      {/* Floating Bottom JS Champ Voice Dock */}
      <FloatingCallDock
        callState={callState}
        isMuted={isMuted}
        errorMessage={errorMessage}
        waveformHeights={waveformHeights}
        onStartCall={startCall}
        onEndCall={endCall}
        onToggleMute={toggleMute}
        onInterrupt={handleInterrupt}
        onResume={resumeFromInactivity}
        onOpenSaveModal={() => setIsSaveModalOpen(true)}
        onQuickPrompt={handleQuickPrompt}
      />

      {/* Save Session / Magic Link Modal */}
      <SaveSessionModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        sessionId={sessionId}
      />
    </div>
  );
};
